import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFilterState, IMethodWrapEvent, LISTING_GET_FILTERS, LISTING_SET_FILTERS, Listing } from '@df/catalog-ui/listing/listing';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { Product } from '@df/catalog/product/product';
import { Events } from '@df/core/service/event';
import { FacetUrlService } from '@df/module-facet-url/core/facet-url.service';
import { NFacetUrl } from '@df/module-facet-url/core/facet.interfaces';
import isEmpty from 'lodash-es/isEmpty';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import take from 'lodash-es/take';

/**
 * Plugin for meta listing
 */
@Injectable({
    providedIn: 'root'
})
export class FacetUrlListingPluginService {
    /**
     * Needs to be injected to avoid problems with too early initialization
     */
    private attributeResource = inject(AttributeResource);
    private events = inject(Events);
    private facetUrlService = inject(FacetUrlService);
    private destroyRef = inject(DestroyRef);

    appInitialize() {
        this.patchGetFilters();
        this.patchSetFilters();
    }

    /**
     * Plugin for methods reading filtrs from url
     * rathert then just from hash we add support to read attributes too
     */
    private patchGetFilters() {
        this.events
            .on(LISTING_GET_FILTERS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((transport: IMethodWrapEvent<IFilterState>) => {
                const facetData = this.retrieveFacetData(transport.listing);

                if (facetData) {
                    const data = transport.cb() || {};
                    // listing read labels not values, lets map it
                    const facetDataFilterState = mapValues(
                        facetData.filter,
                        (attrValue, attrCode) => this.attributeResource.getExisting(attrCode)?.getOptionLabel(attrValue) as string
                    );
                    transport.cb = () => ({ ...data, ...facetDataFilterState });
                }
            });
    }

    /**
     * Plugin for methods reading filtrs from url
     * rathert then just from hash we add support to read attributes too
     */
    private patchSetFilters() {
        this.events
            .on(LISTING_SET_FILTERS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((transport: IMethodWrapEvent<void, Product, IFilterState>) => {
                const category = transport.listing.config.source as Category<ICategoryDataV3>;

                if (category?.type === 'category' && this.facetUrlService.usesFacetUrls(category)) {
                    // identify data part that should b
                    if (transport.data) {
                        let attributeCodes = this.facetUrlService.getFacetAttributes(category, transport.data).filter(attributeCode => {
                            const value = transport.data![attributeCode];
                            return value && Array.isArray(value) && value.length === 1;
                        });

                        // apply components limit
                        if ((this.facetUrlService.getFacetComponentsLimit() || 0) > 0) {
                            attributeCodes = take(attributeCodes, this.facetUrlService.getFacetComponentsLimit());
                        }

                        const facetValues = pick(transport.data, attributeCodes);

                        if (!isEmpty(facetValues)) {
                            transport.data = omit(transport.data, attributeCodes);
                        }

                        this.facetUrlService.setFacetUrl(
                            category,
                            mapValues(facetValues, value => value[0]) // we can take first cause all attrs have only one selection
                        );
                    }
                }
            });
    }

    /**
     * Retrieves facet url data related to listing
     */
    private retrieveFacetData(listing: Listing): NFacetUrl.IFacetData | undefined {
        const categoryId = listing.config.source?.id;

        return categoryId ? this.facetUrlService.retrieveFacetData(categoryId) : undefined;
    }
}
